import {
  coinNameMatchesAsset,
  getWalletSettlement,
  isNativePairAsset,
  isPaymentCoinName,
  resolveEip712VerifyingContract,
  resolveSettlementAssetToken,
  resolveSettlementPaymentToken,
} from './linkedWallet.utils';
import {
  DVP_EIP712_DOMAIN_NAME,
  DVP_EIP712_DOMAIN_VERSION,
} from '../../constants/wallet.constants';
import {
  approveErc20Allowance,
  ensureErc20Allowance,
  getMetaMaskProvider,
  resolveOrderAllowanceRequirement,
  resolveSpenderAddress,
  resolveTokenContractAddress,
  resolveTokenDecimals,
} from './nonCustodialWallet.service';
import { logNonCustodialBalance } from './nonCustodialBalance.debug';
import {
  addUintStrings,
  buildAllowanceBucketKey,
  buildPairFromOrder,
  computeAggregateAllowanceTargets,
  getOpenOrdersForAllowance,
  getUnfilledOrderQuantity,
  reconcileAllowanceAfterOrderCancel as reconcileAllowanceAfterOrderCancelInternal,
} from './nonCustodialAllowance.service';
import { setNonCustodialRpcBusy } from './nonCustodialSession';
import { getDayOrderExpireUnix } from '../../helpers/commonHelper';
import {
  decimalAmountToTokenUnits,
  multiplyHumanDecimals,
  toUint256String,
} from './tokenUnits.utils';

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
const DEFAULT_DVP_ORDER_TTL_SECONDS = 86400;

const isNativeDvpAssetToken = (assetToken) => {
  const normalized = String(assetToken ?? '').trim().toLowerCase();
  return !normalized || normalized === ZERO_ADDRESS;
};

const DVP_ORDER_EIP712_TYPES = {
  EIP712Domain: [
    { name: 'name', type: 'string' },
    { name: 'version', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'verifyingContract', type: 'address' },
  ],
  Order: [
    { name: 'salt', type: 'uint256' },
    { name: 'maker', type: 'address' },
    { name: 'assetToken', type: 'address' },
    { name: 'paymentToken', type: 'address' },
    { name: 'amount', type: 'uint256' },
    { name: 'price', type: 'uint256' },
    { name: 'expiration', type: 'uint256' },
    { name: 'side', type: 'uint8' },
  ],
};

const toTokenUnits = (amount, decimals) => decimalAmountToTokenUnits(amount, decimals);

const normalizeSide = (side) => {
  const parsed = Number(side);
  return parsed === 1 ? 1 : 0;
};

const toChecksumAddress = (web3, address) => {
  if (!address) return '';
  try {
    return web3.utils.toChecksumAddress(String(address).trim());
  } catch {
    return String(address).trim().toLowerCase();
  }
};

const normalizeContractAddress = (web3, address) => {
  const trimmed = String(address ?? '').trim();
  if (!trimmed) return null;
  if (trimmed.toLowerCase() === ZERO_ADDRESS) return ZERO_ADDRESS;
  return toChecksumAddress(web3, trimmed);
};

const resolveEip712Domain = (wallet) => {
  const settlement = getWalletSettlement(wallet);
  const eip712 = wallet?.eip712 ?? settlement?.eip712 ?? null;
  const chainId = Number(
    wallet?.chainId ?? eip712?.chainId ?? eip712?.chain_id,
  );
  const verifyingContract = resolveEip712VerifyingContract(wallet) ?? '';

  if (!Number.isFinite(chainId) || !verifyingContract) {
    return null;
  }

  return {
    name: DVP_EIP712_DOMAIN_NAME,
    version: DVP_EIP712_DOMAIN_VERSION,
    chainId,
    verifyingContract,
  };
};

/**
 * Asset leg of the DvP order (otherCurrency).
 * Known chain-native coins (tpolygon, teth) → zero address when no ERC-20 exists.
 * Security tokens (abs1, aptt) → ERC-20 contract from linked-wallets API.
 */
export const resolveDvpAssetToken = (wallet, selectedPair) => {
  const assetSymbol = String(selectedPair?.otherCurrency ?? '').trim().toLowerCase();
  if (!assetSymbol) return null;

  const fromContracts = resolveTokenContractAddress(wallet, assetSymbol);
  if (fromContracts) return fromContracts;

  const walletCoin = String(wallet?.coinName ?? '').trim().toLowerCase();
  if (coinNameMatchesAsset(walletCoin, assetSymbol)) {
    const fromOwnRow = resolveSettlementAssetToken(wallet);
    if (fromOwnRow) return fromOwnRow;
  }

  if (isNativePairAsset(assetSymbol)) {
    return ZERO_ADDRESS;
  }

  const cachedAsset = String(wallet?.assetTokenAddress ?? '').trim();
  if (cachedAsset) {
    const paymentSymbol = String(selectedPair?.baseCurrency ?? '').trim().toLowerCase();
    const paymentFromContracts = paymentSymbol
      ? resolveTokenContractAddress(wallet, paymentSymbol)
      : null;
    const paymentCandidate =
      paymentFromContracts ||
      (isPaymentCoinName(wallet?.coinName) ||
        coinNameMatchesAsset(wallet?.coinName, paymentSymbol)
        ? String(wallet?.paymentTokenAddress ?? '').trim()
        : '');

    if (
      !paymentCandidate ||
      cachedAsset.toLowerCase() !== paymentCandidate.toLowerCase()
    ) {
      return cachedAsset;
    }
  }

  return null;
};

/**
 * Payment leg of the DvP order (baseCurrency).
 * ERC-20 quote (tpolygon:usdc) → settlement.tokenAddress from linked-wallets API.
 */
export const resolveDvpPaymentToken = (wallet, selectedPair) => {
  const paymentSymbol = String(selectedPair?.baseCurrency ?? '').trim().toLowerCase();
  if (!paymentSymbol) return null;

  const fromContracts = resolveTokenContractAddress(wallet, paymentSymbol);
  if (fromContracts) return fromContracts;

  const walletCoin = String(wallet?.coinName ?? '').trim().toLowerCase();
  if (isPaymentCoinName(walletCoin) || coinNameMatchesAsset(walletCoin, paymentSymbol)) {
    const fromOwnRow =
      String(wallet?.paymentTokenAddress ?? '').trim() ||
      resolveSettlementPaymentToken(wallet);
    if (fromOwnRow) return fromOwnRow;
  }

  const cachedPayment = String(wallet?.paymentTokenAddress ?? '').trim();
  if (cachedPayment) {
    const assetSymbol = String(selectedPair?.otherCurrency ?? '').trim().toLowerCase();
    const assetFromContracts = assetSymbol
      ? resolveTokenContractAddress(wallet, assetSymbol)
      : null;
    const assetCandidate =
      assetFromContracts ||
      (coinNameMatchesAsset(wallet?.coinName, assetSymbol)
        ? String(wallet?.assetTokenAddress ?? '').trim()
        : '');

    if (
      !assetCandidate ||
      isNativeDvpAssetToken(assetCandidate) ||
      cachedPayment.toLowerCase() !== assetCandidate.toLowerCase()
    ) {
      return cachedPayment;
    }
  }

  return null;
};

export const getDvpOrderBlockReason = ({ wallet, selectedPair }) => {
  if (!wallet || !selectedPair) {
    return 'Select a wallet and trading pair before placing an order.';
  }

  if (!resolveEip712Domain(wallet)) {
    return 'Order signing requires wallet chainId and settlement verifyingContract from the linked-wallets API.';
  }

  const assetToken = resolveDvpAssetToken(wallet, selectedPair);
  const paymentToken = resolveDvpPaymentToken(wallet, selectedPair);

  if (assetToken == null) {
    const assetLabel = String(selectedPair?.otherCurrency ?? 'asset').toUpperCase();
    return `Order signing requires settlement.assetToken or tokenContracts.${assetLabel.toLowerCase()} from the linked-wallets API.`;
  }

  if (!paymentToken || paymentToken.toLowerCase() === ZERO_ADDRESS) {
    return 'Order signing requires settlement.tokenAddress on the payment token wallet row from linked-wallets API.';
  }

  if (
    assetToken !== ZERO_ADDRESS &&
    assetToken.toLowerCase() === paymentToken.toLowerCase()
  ) {
    return 'Order signing has the same address for assetToken and paymentToken. Check linked-wallets settlement fields.';
  }

  return null;
};

const resolveQuantityDecimals = async (web3, { selectedPair, wallet }) => {
  const assetToken = resolveDvpAssetToken(wallet, selectedPair);

  if (isNativeDvpAssetToken(assetToken)) {
    return 18;
  }

  return resolveTokenDecimals(web3, {
    tokenAddress: assetToken,
    assetSymbol: selectedPair.otherCurrency,
    wallet,
  });
};

export const computeDvpOrderAmounts = async ({
  web3,
  wallet,
  selectedPair,
  quantity,
  price,
  side,
}) => {
  const assetToken = resolveDvpAssetToken(wallet, selectedPair);
  const paymentToken = resolveDvpPaymentToken(wallet, selectedPair);
  const qty = Number(quantity);
  const px = Number(price);

  if (!(qty > 0) || !(px > 0) || assetToken == null || !paymentToken) {
    return null;
  }

  const quantityDecimals = await resolveQuantityDecimals(web3, { selectedPair, wallet });
  const paymentDecimals = await resolveTokenDecimals(web3, {
    tokenAddress: paymentToken,
    assetSymbol: selectedPair.baseCurrency,
    wallet,
  });

  const qtyStr = String(quantity ?? '').trim();
  const pxStr = String(price ?? '').trim();
  const paymentHuman = multiplyHumanDecimals(qtyStr, pxStr, paymentDecimals);
  const paymentTotalRaw = toTokenUnits(paymentHuman, paymentDecimals);
  const priceUnits = toTokenUnits(pxStr, paymentDecimals);

  // amount = asset-leg quantity (native or ERC-20) for both buy and sell.
  // USDC payment total (paymentTotalRaw) is used only for buy-side ERC-20 allowance.
  const amount = toTokenUnits(qtyStr, quantityDecimals);

  return {
    assetToken,
    paymentToken,
    quantityDecimals,
    paymentDecimals,
    amount: toUint256String(amount),
    price: toUint256String(priceUnits),
    paymentTotalRaw: toUint256String(paymentTotalRaw),
    paymentTotalHuman: paymentHuman,
  };
};

/** ERC-20 allowance before place order — buy pays USDC; sell native skips approve. */
export const resolveDvpAllowanceRequirement = async ({
  web3,
  wallet,
  selectedPair,
  quantity,
  price,
  side,
}) => {
  const normalizedSide = normalizeSide(side);
  const spenderAddress = resolveSpenderAddress(wallet, selectedPair);
  if (!spenderAddress) return null;

  const computed = await computeDvpOrderAmounts({
    web3,
    wallet,
    selectedPair,
    quantity,
    price,
    side,
  });
  if (!computed) return null;

  if (normalizedSide === 0) {
    return {
      assetSymbol: selectedPair.baseCurrency,
      tokenAddress: computed.paymentToken,
      requiredAmount: computed.paymentTotalHuman,
      requiredAmountRaw: computed.paymentTotalRaw,
      dvpAmountRaw: computed.amount,
      spenderAddress,
      wallet,
    };
  }

  if (isNativeDvpAssetToken(computed.assetToken)) {
    return null;
  }

  return {
    assetSymbol: selectedPair.otherCurrency,
    tokenAddress: computed.assetToken,
    requiredAmount: Number(quantity),
    requiredAmountRaw: computed.amount,
    spenderAddress,
    wallet,
  };
};

const resolveAllowanceRequirement = async ({
  web3,
  wallet,
  selectedPair,
  quantity,
  price,
  side,
}) => {
  let requirement = await resolveDvpAllowanceRequirement({
    web3,
    wallet,
    selectedPair,
    quantity,
    price,
    side,
  });

  if (!requirement && normalizeSide(side) === 0) {
    requirement = resolveOrderAllowanceRequirement({
      selectedSide: side,
      selectedPair,
      wallet,
      quantity,
      price,
    });
  }

  return requirement;
};

const resolveOpenOrderAllowanceRequirement = async ({
  web3,
  wallet,
  order,
  fallbackPair,
}) => {
  const pair = buildPairFromOrder(order, fallbackPair);
  if (!pair) return null;

  const unfilledQty = getUnfilledOrderQuantity(order);
  if (!(unfilledQty > 0)) return null;

  const side = Number(order?.side);
  if (!Number.isFinite(side)) return null;

  const price = Number(order?.price);
  if (!(price > 0)) return null;

  return resolveDvpAllowanceRequirement({
    web3,
    wallet,
    selectedPair: pair,
    quantity: unfilledQty,
    price,
    side,
  });
};

const resolveAggregateAllowanceRequirement = async ({
  web3,
  wallet,
  selectedPair,
  quantity,
  price,
  side,
  openOrders,
}) => {
  const singleRequirement = await resolveDvpAllowanceRequirement({
    web3,
    wallet,
    selectedPair,
    quantity,
    price,
    side,
  });
  if (!singleRequirement) return null;

  const targets = await computeAggregateAllowanceTargets({
    openOrders: openOrders ?? [],
    resolveRequirement: (order) =>
      resolveOpenOrderAllowanceRequirement({
        web3,
        wallet,
        order,
        fallbackPair: selectedPair,
      }),
  });

  const bucketKey = buildAllowanceBucketKey(singleRequirement);
  const existingRaw = targets.get(bucketKey)?.requiredAmountRaw ?? '0';
  const aggregateRaw = addUintStrings(existingRaw, singleRequirement.requiredAmountRaw);

  return {
    ...singleRequirement,
    requiredAmountRaw: aggregateRaw,
    orderAllowanceRaw: singleRequirement.requiredAmountRaw,
    aggregateAllowanceRaw: aggregateRaw,
  };
};

/**
 * Must complete before DvP signing on buy orders.
 * Buy: USDC approve → then sign. Sell native: no ERC-20 approve.
 */
export const ensureDvpTradeAllowance = async ({
  web3,
  wallet,
  selectedPair,
  quantity,
  price,
  side,
  ownerAddress,
  onApprovalTxRequested,
}) => {
  const normalizedSide = normalizeSide(side);

  const assetToken = resolveDvpAssetToken(wallet, selectedPair);
  if (normalizedSide === 1 && isNativeDvpAssetToken(assetToken)) {
    logNonCustodialBalance('ensureDvpTradeAllowance:skipped-native-sell', {
      asset: selectedPair.otherCurrency,
    });
    return { skipped: true, approved: true };
  }

  const spenderAddress = resolveSpenderAddress(wallet, selectedPair);
  if (!spenderAddress) {
    throw new Error('Token approval requires settlement.spenderAddress from the linked-wallets API.');
  }

  const openOrders = getOpenOrdersForAllowance({
    pairName: selectedPair?.pair_key,
    wallet,
  });

  let requirement = await resolveAggregateAllowanceRequirement({
    web3,
    wallet,
    selectedPair,
    quantity,
    price,
    side,
    openOrders,
  });

  if (!requirement) {
    requirement = await resolveAllowanceRequirement({
      web3,
      wallet,
      selectedPair,
      quantity,
      price,
      side,
    });
  }

  if (!requirement) {
    if (normalizedSide === 0) {
      throw new Error(
        'Unable to verify USDC approval for this order. Re-select your MetaMask wallet and try again.',
      );
    }
    return { skipped: true, approved: true };
  }

  logNonCustodialBalance('ensureDvpTradeAllowance:start', {
    side: normalizedSide,
    assetSymbol: requirement.assetSymbol,
    requiredAmount: requirement.requiredAmount,
    requiredAmountRaw: requirement.requiredAmountRaw,
    orderAllowanceRaw: requirement.orderAllowanceRaw,
    aggregateAllowanceRaw: requirement.aggregateAllowanceRaw,
    openOrderCount: openOrders.length,
    dvpAmountRaw: requirement.dvpAmountRaw,
    tokenAddress: requirement.tokenAddress,
    spenderAddress: requirement.spenderAddress,
    verifyingContract: resolveEip712VerifyingContract(wallet),
  });

  const result = await ensureErc20Allowance({
    web3,
    ownerAddress,
    requirement,
    onApprovalTxRequested,
  });

  logNonCustodialBalance('ensureDvpTradeAllowance:complete', {
    approved: result.approved,
    transactionHash: result.transactionHash,
    skipped: result.skipped,
  });

  return result;
};

export const reconcileDvpAllowanceAfterCancel = async ({
  web3,
  wallet,
  ownerAddress,
  cancelledOrder,
  fallbackPair,
  onApprovalTxRequested,
}) =>
  reconcileAllowanceAfterOrderCancelInternal({
    web3,
    wallet,
    ownerAddress,
    cancelledOrder,
    fallbackPair,
    onApprovalTxRequested,
    approveErc20Allowance,
    setNonCustodialRpcBusy,
    resolveOrderRequirement: (order) =>
      resolveOpenOrderAllowanceRequirement({
        web3,
        wallet,
        order,
        fallbackPair,
      }),
  });

const resolveDvpExpiration = ({ advanceOrderType, getGTDExpiryTime }) => {
  // GTC: no fixed on-chain expiry.
  if (advanceOrderType === 'GTC') {
    return 0;
  }

  // DAY: settle at 16:30 ET today.
  if (advanceOrderType === 'DAY') {
    const dayExpiry = getDayOrderExpireUnix();
    if (dayExpiry > 0) return dayExpiry;
  }

  // GTD: user-selected upcoming date/time (ET → unix).
  if (advanceOrderType === 'GTD' && typeof getGTDExpiryTime === 'function') {
    const expireAt = getGTDExpiryTime();
    const parsed = Math.floor(new Date(expireAt).getTime() / 1000);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return Math.floor(Date.now() / 1000) + DEFAULT_DVP_ORDER_TTL_SECONDS;
};

const parseWalletRpcError = (error) => {
  const code = error?.code ?? error?.data?.code;
  const message = String(error?.message ?? '');
  if (code === 4001 || /user rejected|user denied/i.test(message)) {
    return new Error('Order signature was rejected in MetaMask.');
  }
  return error instanceof Error ? error : new Error(message || 'Failed to sign order in MetaMask.');
};

/** Hex (no 0x prefix) → decimal string for EIP-712 uint256 salt. */
const hexToDecimalString = (hex) => {
  const digits = [0];
  for (let i = 0; i < hex.length; i += 2) {
    let carry = parseInt(hex.slice(i, i + 2), 16);
    for (let j = 0; j < digits.length; j += 1) {
      const value = digits[j] * 256 + carry;
      digits[j] = value % 10;
      carry = Math.floor(value / 10);
    }
    while (carry > 0) {
      digits.push(carry % 10);
      carry = Math.floor(carry / 10);
    }
  }
  return digits.reverse().join('') || '0';
};

const generateSalt = () => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return hexToDecimalString(hex);
  }
  return String(Date.now());
};

export const signDvpOrderForTrade = async ({
  web3,
  wallet,
  selectedPair,
  ownerAddress,
  quantity,
  price,
  side,
  advanceOrderType,
  getGTDExpiryTime,
}) => {
  const blockReason = getDvpOrderBlockReason({ wallet, selectedPair });
  if (blockReason) throw new Error(blockReason);

  const computed = await computeDvpOrderAmounts({
    web3,
    wallet,
    selectedPair,
    quantity,
    price,
    side,
  });
  if (!computed) {
    throw new Error('Order quantity and price must be greater than zero.');
  }

  const maker = toChecksumAddress(web3, ownerAddress);
  if (!/^0x[a-fA-F0-9]{40}$/.test(maker)) {
    throw new Error('Linked wallet address is invalid. Reconnect MetaMask and try again.');
  }

  const domain = resolveEip712Domain(wallet);
  const { amount, price: priceUnits } = computed;
  const assetToken = normalizeContractAddress(web3, computed.assetToken);
  const paymentToken = normalizeContractAddress(web3, computed.paymentToken);
  const normalizedSide = normalizeSide(side);
  const expiration = resolveDvpExpiration({ advanceOrderType, getGTDExpiryTime });

  const message = {
    salt: generateSalt(),
    maker,
    assetToken,
    paymentToken,
    amount: toUint256String(amount),
    price: toUint256String(priceUnits),
    expiration: String(expiration),
    side: normalizedSide,
  };

  const typedData = {
    types: DVP_ORDER_EIP712_TYPES,
    primaryType: 'Order',
    domain,
    message,
  };

  logNonCustodialBalance('signDvpOrderForTrade:request', {
    domain,
    message,
    ownerAddress,
  });

  const provider = getMetaMaskProvider() ?? web3?.currentProvider;
  if (!provider?.request) {
    throw new Error('MetaMask provider is not available. Reconnect your wallet and try again.');
  }

  let signature;
  try {
    signature = await provider.request({
      method: 'eth_signTypedData_v4',
      params: [ownerAddress, JSON.stringify(typedData)],
    });
  } catch (error) {
    throw parseWalletRpcError(error);
  }

  if (!signature) {
    throw new Error('MetaMask did not return an order signature.');
  }

  const dvpOrder = {
    ...message,
    side: normalizedSide,
    signature: String(signature),
  };

  logNonCustodialBalance('signDvpOrderForTrade:success', {
    maker,
    assetToken,
    paymentToken,
    assetSymbol: selectedPair?.otherCurrency,
    paymentSymbol: selectedPair?.baseCurrency,
    amount,
    price: priceUnits,
    paymentTotalRaw: computed.paymentTotalRaw,
    side: normalizedSide,
    expiration,
  });

  return dvpOrder;
};
